import Vue from 'vue';
import { createPopper } from '@popperjs/core';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';

function getConfig(binding) {
  let tooltipText;
  let maxWidth;
  let delay = 0;
  const isArrowHidden = binding.modifiers?.noarrow ?? false;
  const isHighlightTrigger = binding.modifiers?.dashed ?? false;
  const isHtmlContent = binding.modifiers?.html ?? false;

  if (binding.modifiers?.multiline) {
    maxWidth = 200;
  }

  if (typeof binding.value === 'string') {
    tooltipText = binding.value;
  } else {
    tooltipText = binding.value?.text;
    delay = binding.value?.delay;
    maxWidth = binding.value?.maxWidth ?? maxWidth;
  }
  const placement = binding.arg || 'auto';
  return {
    tooltipText,
    placement,
    delay,
    isArrowHidden,
    isHighlightTrigger,
    isHtmlContent,
    maxWidth,
  };
}

function showTooltip(el, binding) {
  const { tooltipText, placement, isArrowHidden, isHtmlContent, maxWidth } = getConfig(binding);
  if (!tooltipText) {
    return;
  }

  // Clean up any stale tooltip before creating a new one.
  // This prevents orphaned popper-tooltip divs when elements
  // are repositioned without firing mouseout (e.g. instant layout).
  hideTooltip(el);

  const contentEl = document.createElement('div');

  if (isHtmlContent) {
    contentEl.innerHTML = DOMPurify.sanitize(tooltipText);
  } else {
    contentEl.innerText = tooltipText;
  }

  if (!isArrowHidden) {
    const arrowEl = document.createElement('div');
    arrowEl.dataset.popperArrow = '';
    contentEl.appendChild(arrowEl);
  }
  contentEl.classList.add('popper-tooltip');
  // eslint-disable-next-line tap/no-raw-text-js
  contentEl.style.maxWidth = maxWidth ? `${maxWidth}px` : 'auto';
  if (isHtmlContent) {
    // eslint-disable-next-line tap/no-raw-text-js
    contentEl.style.textAlign = 'left';
  }

  document.body.appendChild(contentEl);
  el.popperInstance = createPopper(el, contentEl, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 5],
        },
      },
    ],
  });
}

function hideTooltip(el) {
  const { popperInstance } = el;
  if (popperInstance) {
    if (popperInstance.state?.elements?.popper) {
      document.body.removeChild(popperInstance.state.elements.popper);
    }
    popperInstance.destroy();
    el.popperInstance = null;
  }
}

function updateTooltip(el, binding) {
  const { tooltipText, isHighlightTrigger } = getConfig(binding);
  const { popperInstance } = el;
  if (popperInstance) {
    popperInstance.state.elements.popper.firstChild.textContent = tooltipText;
    popperInstance.update();
  }
  if (isHighlightTrigger && tooltipText) {
    el.classList.add('popper-dashed');
  } else {
    el.classList.remove('popper-dashed');
  }
  // Remove old mouse event and register new one
  el.removeEventListener('mouseover', el.out.mouseover);
  el.out.mouseover = (event) => {
    if (!event.buttons) {
      showTooltip(el, binding);
    }
  };
  el.addEventListener('mouseover', el.out.mouseover);
}

function handleMouseOver(el, event, binding) {
  if (!event.buttons) {
    showTooltip(el, binding);
  }
}

Vue.directive('tooltip', {
  bind(el, binding) {
    const { tooltipText, delay, isHighlightTrigger } = getConfig(binding);
    if (isHighlightTrigger && tooltipText) {
      el.classList.add('popper-dashed');
    }
    const handler = delay
      ? debounce(handleMouseOver, delay, {
          leading: false,
          trailing: true,
        })
      : handleMouseOver;
    if (delay) {
      el.delayHandler = handler;
    }
    el.out = {
      mouseover: (e) => handler(el, e, binding),
      mouseleave: () => {
        if (delay) {
          handler.cancel();
        }
        hideTooltip(el);
      },
    };
    el.addEventListener('mouseover', el.out.mouseover);
    el.addEventListener('mouseout', el.out.mouseleave);
  },

  update(el, binding) {
    updateTooltip(el, binding);
  },

  unbind(el) {
    if (el?.delayHandler) {
      el.delayHandler.cancel();
    }
    el.removeEventListener('mouseover', el.out?.mouseover);
    el.removeEventListener('mouseout', el.out?.mouseleave);
    hideTooltip(el);
  },
});
